import React from "react";

import { Avatar, ButtonStandard, ModalBase, Skeleton, Text } from "@performyard/py-components";

import { useSignatureRequestTemplateDetail } from "../../../api/hooks/defs/signature-request-templates";

import avatarURL from "../../../utils/avatarURL";

import { getAssigneeWildcard } from "./todoForm";

const describeSigner = (signer: string | null): string => {
	if (!signer) return "Signer";
	return getAssigneeWildcard(signer)?.name ?? "Signer";
};

/**
 * Shows the signature steps of the signature request template attached to a to-do, with
 * the option to detach it. Step data comes from the legacy detail endpoint; the fallback
 * name lets the modal render even while (or if) that fetch fails.
 *
 * A to-do can outlive the signature request template it points at, so a failed read is
 * reported as an unavailable document rather than as a template with no steps — and the
 * detach button stays available, since detaching is the fix.
 *
 * The frame is sized on `ModalBase` rather than on a padded child: the React app has no
 * border-box reset, so `w-[432px]` and `px-6` on the same element would render 480px
 * wide. The launch wizard's copy of this modal is laid out the same way.
 */
const SignatureTemplateDetailsModal = ({
	templateId,
	templateName,
	onClose,
	onRemove,
}: {
	templateId: string;
	/** Name already known by the editor row, shown while the detail loads. */
	templateName: string;
	onClose: () => void;
	onRemove: () => void;
}) => {
	// The modal reports the failure inline, so the generic query toast is suppressed.
	const { data, isLoading, isError } = useSignatureRequestTemplateDetail(templateId, {
		meta: { disableDefaultErrorHandling: true },
	});

	const signers = data?.signers ?? [];

	return (
		<ModalBase isOpen onClose={onClose} dangerouslySetClassName="w-[432px] max-w-[calc(100vw-32px)]">
			<div className="flex flex-col gap-4 px-6 pb-6">
				<div className="flex flex-col">
					<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
						<span className="py-line-clamp-1">{data?.name ?? templateName}</span>
					</Text>
					<Text size="paragraph-2" weight="regular" color="neutral-9" dangerouslySetClassName="text-center">
						Signature Steps
					</Text>
				</div>

				{isLoading ? (
					<div className="flex flex-col gap-2" data-testid="signature-steps-loading">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</div>
				) : isError ? (
					<Text size="paragraph-3" weight="regular" color="neutral-6" data-testid="signature-steps-error">
						This signature request is no longer available. Remove it from the to-do and attach another
						document.
					</Text>
				) : signers.length === 0 ? (
					<Text size="paragraph-3" weight="regular" color="neutral-6">
						This template has no signature steps.
					</Text>
				) : (
					<ol
						className="m-0 flex list-none flex-col divide-y divide-solid divide-neutral-2 p-0"
						data-testid="signature-steps"
					>
						{signers.map((step, index) => {
							const wildcard = step.signer ? getAssigneeWildcard(step.signer) : undefined;
							return (
								<li key={index} className="flex items-start gap-2 p-2">
									<Text
										size="paragraph-2"
										weight="regular"
										color="neutral-7"
										dangerouslySetClassName="w-7 shrink-0"
									>
										{`${index + 1}.`}
									</Text>
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex h-7 items-center gap-1">
											{wildcard ? (
												<Avatar
													size={20}
													url={avatarURL(wildcard.avatarInitials, 0)}
													name={wildcard.name}
												/>
											) : (
												<Avatar size={20} id={step.signer ?? undefined} alt="" />
											)}
											<Text
												size="paragraph-3"
												weight="regular"
												color="neutral-9"
												wrap={false}
												dangerouslySetClassName="min-w-0 py-text-truncate"
											>
												{describeSigner(step.signer)}
											</Text>
										</div>
										{step.fields.length > 0 ? (
											step.fields.map((field, fieldIndex) => (
												<Text
													key={fieldIndex}
													size="paragraph-3"
													weight="regular"
													color="neutral-6"
												>
													<span className="py-line-clamp-1">{`· ${field}`}</span>
												</Text>
											))
										) : (
											<Text size="paragraph-3" weight="regular" color="neutral-4">
												· No Signature Instructions
											</Text>
										)}
									</div>
								</li>
							);
						})}
					</ol>
				)}

				<div className="flex justify-center gap-2 pt-2">
					<div className="w-fit">
						<ButtonStandard variant="outline" color="neutral" size="large" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-fit">
						<ButtonStandard
							variant="solid"
							color="red"
							size="large"
							onClick={onRemove}
							data-testid="remove-signature-template"
						>
							Remove Signature Request
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default SignatureTemplateDetailsModal;
